import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { ChevronLeftIcon, ChevronRightIcon, LockIcon, PlusIcon, SmartphoneIcon, SunIcon } from 'lucide-react';
import { ADMIN_BASE } from '../../utils/hosts';
import { adminSections } from '../../data/adminSections';
import { screens, useMediaQuery } from '../../hooks/useMediaQuery';
import { PageHeader } from '../ui/PageHeader';
import { Spinner } from '../../ui/Spinner';
import { LoadFailed } from '../ui/LoadState';
import { useAdmin } from '../AdminContext';
import { btnGhost, btnIcon, btnPrimary, btnSecondary } from '../ui/styles';
import { undoToast } from '../ui/undoToast';
import { newRequestId } from '../../core/api/http';
import { useAgenda } from './useAgenda';
import { AgendaTimeline } from './AgendaTimeline';
import { AppointmentDetail } from './AppointmentDetail';
import { NewAppointmentForm } from './NewAppointmentForm';
import { BlockForm } from './BlockForm';
import { CalendarSync } from './CalendarSync';
import { MoveConfirm } from './MoveConfirm';
import type { DragTarget } from './useTimelineDrag';
import { asView, blockLabel, durationLabel, stateOf, weekOf } from './agendaRules';
import { dayLoad, isActive } from '../../core/rules/agenda';
import { OWNER_STAFF_ID, performerIssue, performerIssueText, salonDayFor, staffList, staffName } from '../../core/rules/salonDays';
import { StaffDot } from './StaffDot';
import { AbsencePanel } from './AbsencePanel';
import { useCan } from '../useCan';
import { dayLong, fromIso, isIsoDate, shiftDate, today, weekdayShort } from '../../core/format/dates';
import { plural } from '../../core/format/text';
import type { AgendaBlock } from '../../core/types/agenda';
type Side = {
  kind: 'none';
} | {
  kind: 'new';
  date: string;
  time: string;
} | {
  kind: 'block';
  date: string;
  time: string;
} | {
  kind: 'sync';
} | {
  kind: 'blockDetail';
  block: AgendaBlock;
};

/**
 * De agenda van de salon. Dag (standaard, en altijd op de telefoon) of week. Datum, weergave
 * en gekozen afspraak staan in de URL, zodat terug-knop en delen werken.
 */
export function Agenda() {
  const {
    site,
    index
  } = useAdmin();
  const {
    canAll,
    staffId
  } = useCan();
  // De hele salon zien, of alleen eigen rijen (de server stuurt ook niets anders).
  const seesAll = canAll('agenda.view');
  // Overal inplannen: lege tijd aanklikken, slepen, reeksen, blokkeren.
  const plansAll = canAll('agenda.plan');
  const managesRoster = canAll('agenda.roster');
  const [params, setParams] = useSearchParams();
  const date = isIsoDate(params.get('datum')) ? params.get('datum') as string : today();
  const view = asView(params.get('weergave'));
  const selectedId = params.get('afspraak');
  const [side, setSide] = useState<Side>({
    kind: 'none'
  });
  // Een gesleepte afspraak die nog bevestigd moet worden (de klant krijgt bericht).
  const [pendingMove, setPendingMove] = useState<DragTarget | null>(null);
  const agenda = useAgenda(date);
  const range = agenda.range;
  const week = weekOf(date);
  const wide = useMediaQuery(screens.md);
  const showWeek = view === 'week' && wide;
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => v === null ? next.delete(k) : next.set(k, v));
    setParams(next, {
      replace: true
    });
  };
  const select = (id: string | null) => {
    setSide({
      kind: 'none'
    });
    set({
      afspraak: id
    });
  };
  // Op de telefoon is het altijd een dag, ook met `?weergave=week`: dan per dag bladeren.
  const step = showWeek ? 7 : 1;

  // Een collega ziet alleen haar eigen afspraken en blokkades (de server stuurt ook niets anders).
  const team = staffList(site.staff);
  const shared = team.length > 1;
  // Filter "Iedereen / per persoon" (alleen Sarah, gedeelde salon). In de URL, zodat terug werkt.
  const wieParam = params.get('wie');
  const who = seesAll && shared && wieParam && team.some(s => s.id === wieParam) ? wieParam : null;
  const mine = <T extends {
    staffId: string;
  },>(list: T[]) => !seesAll ? list.filter(x => x.staffId === staffId) : who ? list.filter(x => (x.staffId || OWNER_STAFF_ID) === who) : list;
  const appointments = mine(range?.appointments ?? []);
  const blocks = mine(range?.blocks ?? []);
  // In de legenda: wie er actief is, plus wie (gestopt) nog afspraken in deze week heeft.
  const legend = team.filter(s => s.active || (range?.appointments ?? []).some(a => a.staffId === s.id));
  const selected = selectedId ? appointments.find(a => a.id === selectedId) : undefined;
  const moving = pendingMove ? appointments.find(a => a.id === pendingMove.id) : undefined;

  // Ander scherm (datum of weergave): een openstaande verzetting hoort daar niet meer bij.
  useEffect(() => setPendingMove(null), [date, view]);
  const cancelMove = useCallback(() => setPendingMove(null), []);
  const confirmMove = async () => {
    if (!pendingMove || !moving) return;
    const {
      id,
      date: to,
      time,
      conflicts,
      outside
    } = pendingMove;
    // Mislukt (bijv. 409): de melding komt uit useAgenda en de week wordt ververst.
    await agenda.move(id, {
      date: to,
      time,
      staffId: moving.staffId,
      allowOverlap: conflicts.length > 0 || outside
    });
    setPendingMove(null);
  };
  const ofDay = appointments.filter(a => a.date === date);
  const salonDay = salonDayFor(site.openingHours, site.salonExceptions, date);
  // Bezetting voor wie er die dag werkt; een collega altijd voor zichzelf.
  const load = dayLoad(site.openingHours, date, appointments, blocks, {
    exceptions: site.salonExceptions,
    ...(seesAll ? {} : {
      staffId
    })
  });
  const notMyDay = !seesAll && salonDay !== null && salonDay.staffId !== staffId;
  const requests = ofDay.filter(a => isActive(a) && a.status === 'aanvraag');
  // Klopt de uitvoerder nog met de salondag (dag gewisseld, behandeling niet meer bij haar)?
  const mismatched = seesAll && shared ? ofDay.filter(a => isActive(a) && performerIssue(a, site.openingHours, site.salonExceptions, team)) : [];
  const cancelled = ofDay.filter(a => a.outcome === 'geannuleerd');
  const open = (s: Side) => {
    set({
      afspraak: null
    });
    setSide(s);
  };
  const hasSide = side.kind !== 'none' || Boolean(selected);
  const closeSide = useCallback(() => {
    setSide({
      kind: 'none'
    });
    setParams(p => {
      const next = new URLSearchParams(p);
      next.delete('afspraak');
      return next;
    }, {
      replace: true
    });
  }, [setParams]);

  // Een afspraak uit de URL die niet (meer) in deze week staat: melden en loslaten, niet stil niets tonen.
  useEffect(() => {
    if (!range || !selectedId || selected) return;
    toast('Deze afspraak staat niet in deze week', {
      description: 'Hij is verzet of bestaat niet meer.'
    });
    closeSide();
  }, [range, selectedId, selected, closeSide]);

  // Focus naar het paneel bij openen (op de telefoon is het volledig scherm), en terug naar de
  // knop die het opende bij sluiten. Zo raken toetsenbord en schermlezer de draad niet kwijt.
  const panelRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const panelKey = selected ? `a-${selected.id}` : side.kind;
  useEffect(() => {
    if (panelKey === 'none') {
      openerRef.current?.focus();
      openerRef.current = null;
      return;
    }
    if (!openerRef.current && document.activeElement instanceof HTMLElement && !panelRef.current?.contains(document.activeElement)) {
      openerRef.current = document.activeElement;
    }
    // Naast de agenda (lg+) niet meescrollen; op kleinere schermen is het paneel het scherm.
    panelRef.current?.focus({
      preventScroll: window.matchMedia(screens.lg).matches
    });
  }, [panelKey]);

  // Escape sluit het paneel (op de telefoon beslaat dat het hele scherm). Niet als je in een veld typt.
  useEffect(() => {
    if (!hasSide) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const t = e.target as HTMLElement | null;
      if (t && ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName)) return;
      closeSide();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hasSide, closeSide]);
  const sidePanel = selected ? <AppointmentDetail key={selected.id} appointment={selected} appointments={appointments} blocks={blocks} agenda={agenda} onClose={() => select(null)} data-id="element-132" data-mp="2rf23vqok9emj" /> : side.kind === 'new' ? <NewAppointmentForm initialDate={side.date} initialTime={side.time} appointments={appointments} blocks={blocks} onSubmit={agenda.add} onSubmitSeries={plansAll ? agenda.addSeries : undefined} onDone={a => select(a.id)} onCancel={() => setSide({
    kind: 'none'
  })} data-id="element-133" data-mp="31e14clz6a3yg" /> : side.kind === 'block' ? <BlockForm initialDate={side.date} initialTime={side.time} appointments={appointments} onSubmit={agenda.addBlock} onDone={() => setSide({
    kind: 'none'
  })} onCancel={() => setSide({
    kind: 'none'
  })} data-id="element-134" data-mp="3p3date347xx2" /> : side.kind === 'sync' ? <CalendarSync onClose={() => setSide({
    kind: 'none'
  })} data-id="element-135" data-mp="cj7wehhkdc4y" /> : side.kind === 'blockDetail' ? <div className="space-y-4" data-id="element-136" data-mp="3trv5twk9llic">
      <h2 className="flex items-center gap-2 font-display text-[1.4rem] font-light text-ink" data-id="element-137" data-mp="3tql7r7dewdbp">
        <LockIcon className="h-4 w-4 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-138" data-mp="2kaaznffpyq6" />
        {blockLabel(side.block.reason, side.block.label)}
      </h2>
      <p className="text-[14px] text-ink" data-id="element-139" data-mp="3pq189x5d8xme">
        <span className="capitalize" data-id="element-140" data-mp="3pr2umxru16jf">{dayLong(side.block.date)}</span>, {side.block.time} · {durationLabel(side.block.durationMinutes)}
      </p>
      <div className="flex gap-2" data-id="element-141" data-mp="3azaggp3rxyja">
        <button type="button" className={btnSecondary} disabled={agenda.isBusy(side.block.id)} onClick={async () => {
        const {
          id: _id,
          ...block
        } = side.block;
        if (!(await agenda.removeBlock(side.block))) return;
        setSide({
          kind: 'none'
        });
        // Zelfde patroon als elders in het beheer: meteen doen, even "Ongedaan maken".
        undoToast('Blokkade opgeheven', () => void agenda.addBlock({
          ...block,
          requestId: newRequestId()
        }), {
          description: 'Deze tijd is weer te boeken in de webshop.'
        });
      }} data-id="element-142" data-mp="3fzonq4xjx4g9">
          Blokkade opheffen
        </button>
        <button type="button" onClick={() => setSide({
        kind: 'none'
      })} className={btnGhost} data-id="element-143" data-mp="103ybruz5dbm1">
          Sluiten
        </button>
      </div>
    </div> : null;
  return <div data-id="element-144" data-mp="1529pwhwh68a2">
      <PageHeader title="Agenda" description={`${dayLong(date).replace(/^./, c => c.toUpperCase())} · ${plural(ofDay.filter(isActive).length, 'afspraak', 'afspraken')}`} actions={plansAll && <>
            <button type="button" onClick={() => open({
        kind: 'new',
        date,
        time: ''
      })} className={btnPrimary} data-id="element-146" data-mp="1j2amvm9mcvk9">
              <PlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-147" data-mp="1nukkzpkw11xm" />
              Nieuwe afspraak
            </button>
            <button type="button" onClick={() => open({
        kind: 'block',
        date,
        time: ''
      })} className={btnSecondary} data-id="element-148" data-mp="38i4xjggtouvu">
              <LockIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-149" data-mp="33j0lmi2ikj1t" />
              Tijd blokkeren
            </button>
            <Link to={`${ADMIN_BASE}${adminSections.mijnDag.path}`} className={btnSecondary} data-id="element-150" data-mp="3da4seui1cxlk">
              <SunIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-151" data-mp="2pkk6q3uhzm8l" />
              Mijn dag
            </Link>
          </>} data-id="element-145" data-mp="154vtk6g8w3n0" />

      {/* Navigatie */}
      <div className="mb-4 flex flex-wrap items-center gap-2" data-id="element-152" data-mp="2zj9olwme96ou">
        <button type="button" onClick={() => set({
        datum: shiftDate(date, -step),
        afspraak: null
      })} className={btnIcon} aria-label={showWeek ? 'Vorige week' : 'Vorige dag'} data-id="element-153" data-mp="2zkuj5m8pkpmp">
          <ChevronLeftIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-154" data-mp="erzgryf0ya0e" />
        </button>
        <button type="button" onClick={() => set({
        datum: today(),
        afspraak: null
      })} className={btnSecondary} data-id="element-155" data-mp="9swcig6vc7th">
          Vandaag
        </button>
        <button type="button" onClick={() => set({
        datum: shiftDate(date, step),
        afspraak: null
      })} className={btnIcon} aria-label={showWeek ? 'Volgende week' : 'Volgende dag'} data-id="element-156" data-mp="gzi822855n0m">
          <ChevronRightIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-157" data-mp="qw3duprzj25i" />
        </button>
        <div role="radiogroup" aria-label="Weergave" className="ml-auto hidden rounded-full bg-sand p-1 md:flex" data-id="element-158" data-mp="vsc1yso5u5ot">
          {(['dag', 'week'] as const).map(v => <button key={v} type="button" role="radio" aria-checked={view === v} onClick={() => set({
          weergave: v === 'dag' ? null : v
        })} className={`rounded-full px-4 py-1.5 text-[13px] capitalize transition-colors duration-150 ease-soft ${view === v ? 'bg-white text-ink shadow-sm' : 'text-muted hover:text-ink'}`} data-id="element-159" data-mp="36sjpr55460c">
              {v}
            </button>)}
        </div>
        {/* Eenmalig instellen: hoort bij de weergave, niet tussen de hoofdknoppen. */}
        <button type="button" onClick={() => open({
        kind: 'sync'
      })} className={`${btnGhost} ml-auto md:ml-0`} data-id="element-160" data-mp="1to7dwx2tp7vf">
          <SmartphoneIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-161" data-mp="1oo1rpwfbodkq" />
          Op je telefoon
        </button>
      </div>

      {/* Gedeelde salon: legenda en filter in één. De kleur staat altijd naast de naam (niet alleen kleur). */}
      {seesAll && shared && <div role="radiogroup" aria-label="Toon afspraken van" className="-mx-1 mb-4 flex flex-wrap items-center gap-1.5 px-1" data-id="element-162" data-mp="1a06wa89xjv3y">
          {[{
        id: null,
        name: 'Iedereen'
      }, ...legend].map(s => {
        const on = who === s.id;
        return <button key={s.id ?? 'iedereen'} type="button" role="radio" aria-checked={on} onClick={() => set({
          wie: s.id,
          afspraak: null
        })} className={`inline-flex min-h-9 items-center gap-1.5 rounded-full border px-3 text-[13px] transition-colors duration-150 ease-soft ${on ? 'border-ink bg-ink text-shell' : 'border-line bg-white text-ink hover:border-ink/30'}`} data-id="element-163" data-mp="1113ct36updox">
                {s.id && <StaffDot staff={team} id={s.id} size="sm" data-id="element-164" data-mp="2mcluzis72h4r" />}
                {s.name}
              </button>;
      })}
        </div>}

      {/* Dagstrook: snel wisselen binnen de week, met het aantal afspraken per dag. */}
      <div className={`mb-4 grid grid-cols-7 gap-1 ${showWeek ? 'hidden' : ''}`} data-id="element-165" data-mp="2dcywp5urhrfx">
        {week.days.map(d => {
        const n = appointments.filter(a => a.date === d && isActive(a)).length;
        const on = d === date;
        return <button key={d} type="button" aria-pressed={on} aria-label={`${dayLong(d)}, ${plural(n, 'afspraak', 'afspraken')}`} onClick={() => set({
          datum: d,
          afspraak: null
        })} className={`rounded-md py-2 text-center transition-colors duration-150 ease-soft ${on ? 'bg-ink text-shell' : 'bg-white text-ink ring-1 ring-line hover:ring-ink/30'}`} data-id="element-166" data-mp="3sso5w41cw5yc">
              <span className="block text-[11px] capitalize opacity-80" data-id="element-167" data-mp="1vva0vmy6b7np">{weekdayShort(fromIso(d))}</span>
              <span className="block font-display text-[17px] leading-tight" data-id="element-168" data-mp="1r2qfw4x5pa8v">{fromIso(d).getDate()}</span>
              <span className="block text-[10px] opacity-80" data-id="element-169" data-mp="24tbz5uqivm7a">{n ? n : '\u00a0'}</span>
            </button>;
      })}
      </div>

      {agenda.status === 'loading' && !range ? <div className="flex items-center gap-2 py-16 text-[14px] text-muted" role="status" data-id="element-170" data-mp="2errs839415jf">
          <Spinner data-id="element-171" data-mp="29v1dpkcyei04" /> Agenda laden…
        </div> : agenda.status === 'error' && !range ? <LoadFailed title="De agenda laden lukte niet" onRetry={agenda.reload} data-id="element-172" data-mp="kg4lbicy1emo" /> : <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]" data-id="element-173" data-mp="fgry2wi5u5jk">
          <div className="min-w-0" data-id="element-174" data-mp="2fihs38z09tr4">
            {/* Een latere verversing mislukt: de oude stand blijft staan, maar dat moet ze weten. */}
            {agenda.status === 'error' && <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md bg-blush/60 px-3 py-2.5 text-[13px] text-ink" role="alert" data-id="element-175" data-mp="2oiwgih79noyu">
                Bijwerken lukte niet; je ziet de laatst bekende stand.
                <button type="button" onClick={agenda.reload} className={btnGhost} data-id="element-176" data-mp="2yhlxm7rc95e1">
                  Opnieuw proberen
                </button>
              </div>}
            {/* Eén tijdlijn tegelijk: de week alleen vanaf tablet, op de telefoon altijd de dag. */}
            <AgendaTimeline days={showWeek ? week.days : [date]} showHeader={showWeek} appointments={appointments} blocks={blocks} selectedId={selectedId} onSelect={select} onSelectBlock={b => open({
          kind: 'blockDetail',
          block: b
        })} onEmpty={(d, t) => plansAll ? open({
          kind: 'new',
          date: d,
          time: t
        }) : undefined} onMove={plansAll ? setPendingMove : undefined} pending={pendingMove} team={shared ? team : []} data-id="element-177" data-mp="3c8h9zu11q4md" />
            {/* Op de telefoon is het zijpaneel verborgen; daar de uitleg onder de lege tijdlijn. */}
            {!showWeek && load.open && !ofDay.some(isActive) && <p className="mt-3 text-[13px] text-muted lg:hidden" data-id="element-178" data-mp="iv77guq3rlhq">
                {plansAll ? 'Nog niets gepland. Tik op een lege plek om iemand in te plannen.' : notMyDay ? 'Op deze dag werk je niet.' : 'Nog niets gepland op deze dag.'}
              </p>}
            {/* Slepen op de telefoon is niet te zien; één regel zegt hoe het werkt. */}
            {plansAll && ofDay.some(isActive) && !pendingMove && <p className="mt-3 text-[13px] text-muted lg:hidden" data-id="element-179" data-mp="11o1dgz5r039t">Houd een afspraak even ingedrukt om hem naar een andere tijd te slepen.</p>}
          </div>

          <aside aria-label={sidePanel ? 'Afspraak' : 'Dagoverzicht'} className={`${hasSide ? 'fixed inset-0 z-overlay overflow-y-auto bg-shell p-5 lg:static lg:z-auto lg:bg-transparent lg:p-0' : 'hidden lg:block'}`} data-id="element-180" data-mp="1ghxr1dinckcn">
            <div ref={panelRef} tabIndex={-1} className="rounded-lg border border-line bg-white p-5 focus:outline-none lg:sticky lg:top-24" data-id="element-181" data-mp="16j877f76wptr">
              {sidePanel ?? <div className="space-y-5" data-id="element-182" data-mp="1zajgng2yneos">
                  <div data-id="element-183" data-mp="24931te9qcmg3">
                    <h2 className="text-[15px] font-medium text-ink capitalize" data-id="element-184" data-mp="249cuxaq2n9q0">{dayLong(date)}</h2>
                    <p className="mt-0.5 text-[13px] text-muted" data-id="element-185" data-mp="1pcltyx0r1pya">
                      {notMyDay ? 'Op deze dag werk je niet' : load.open ? `${durationLabel(load.booked)} geboekt van ${durationLabel(load.available)} open` : 'De salon is dicht'}
                    </p>
                    {seesAll && shared && salonDay && <p className="mt-0.5 text-[13px] text-muted" data-id="element-186" data-mp="2x1y7wsppddjn">
                        In de salon: <span className="text-ink" data-id="element-187" data-mp="2s3d98xq3n1xb">{staffName(team, salonDay.staffId)}</span>
                        {salonDay.exception ? ' (afwijkende dag)' : ''}
                      </p>}
                  </div>
                  {managesRoster && shared && salonDay && <AbsencePanel key={date} date={date} staffId={salonDay.staffId} team={team} appointments={range?.appointments ?? []} agenda={agenda} data-id="element-188" data-mp="2ee32px85p825" />}
                  {mismatched.length > 0 && <div className="rounded-md bg-blush/60 px-3 py-2.5 text-[13px] text-ink" role="status" data-id="element-189" data-mp="2n6whzunls7g0">
                      <p className="font-medium" data-id="element-190" data-mp="2idbw30zab3x6">Uitvoerder klopt niet meer</p>
                      <ul className="mt-1 space-y-0.5" data-id="element-191" data-mp="1unpvqa76einl">
                        {mismatched.map(a => {
                  const issue = performerIssue(a, site.openingHours, site.salonExceptions, team);
                  return <li key={a.id} data-id="element-192" data-mp="gnoaqrdrc1x6">
                              <button type="button" onClick={() => select(a.id)} className="text-left underline-offset-2 hover:underline" data-id="element-193" data-mp="gozb0h7zurjj">
                                {a.time} {a.customer.name}: {issue ? performerIssueText[issue].toLowerCase() : ''}
                              </button>
                            </li>;
                })}
                      </ul>
                      <p className="mt-1 text-muted" data-id="element-194" data-mp="zde2f5m847kv">Verzet de afspraak, of pas de salondag aan in Instellingen.</p>
                    </div>}
                  {requests.length > 0 && <p className="rounded-md bg-blush/60 px-3 py-2.5 text-[13px] text-ink" data-id="element-195" data-mp="18fdl02cvzjpx">{plural(requests.length, 'aanvraag wacht', 'aanvragen wachten')} op bevestiging.</p>}
                  <ul className="space-y-1" data-id="element-196" data-mp="3a7igtoxib15x">
                    {ofDay.filter(a => a.outcome !== 'geannuleerd').map(a => <li key={a.id} data-id="element-197" data-mp="1unj21rjjetf2">
                          <button type="button" onClick={() => select(a.id)} className="flex w-full items-baseline gap-3 rounded-md px-2 py-2 text-left transition-colors duration-150 ease-soft hover:bg-shell" data-id="element-198" data-mp="1updlf1f5udh1">
                            <span className="flex w-11 shrink-0 items-center gap-1 text-[13px] tabular-nums text-muted" data-id="element-199" data-mp="1kv3g6j54dkow">
                              {a.time}
                            </span>
                            {seesAll && shared && <StaffDot staff={team} id={a.staffId} size="sm" className="self-center" data-id="element-200" data-mp="xx9zzzj4v5m1" />}
                            <span className="min-w-0 flex-1" data-id="element-201" data-mp="11yc00ikvjlp2">
                              <span className="block truncate text-[14px] text-ink" data-id="element-202" data-mp="11yvhjy6m482k">{a.customer.name}</span>
                              <span className="block truncate text-[12px] text-muted" data-id="element-203" data-mp="2s8x2m889oacz">
                                {index.treatmentBySlug(a.treatmentSlug)?.name ?? a.treatmentSlug} · {stateOf(a).label}
                                {seesAll && shared ? ` · ${staffName(team, a.staffId)}` : ''}
                              </span>
                            </span>
                          </button>
                        </li>)}
                  </ul>
                  {ofDay.length === 0 && load.open && <p className="text-[13px] text-muted" data-id="element-204" data-mp="runj9a07akga">Nog niets gepland. Klik op een lege plek in de tijdlijn om iemand in te plannen.</p>}
                  {cancelled.length > 0 && <p className="border-t border-line pt-3 text-[12px] text-muted" data-id="element-205" data-mp="nvfs36wgi60z">
                      Geannuleerd: {cancelled.map(a => `${a.customer.name} (${a.time})`).join(', ')}
                    </p>}
                </div>}
            </div>
          </aside>
        </div>}

      <AnimatePresence data-id="element-206" data-mp="16s7kc5tz7vh6">
        {pendingMove && moving && <MoveConfirm key={pendingMove.id} appointment={moving} target={pendingMove} busy={agenda.isBusy(pendingMove.id)} onConfirm={() => void confirmMove()} onCancel={cancelMove} data-id="element-207" data-mp="16qmpef3r2ais" />}
      </AnimatePresence>
    </div>;
}